<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="ml-2 mr-2">
      <validation-observer ref="gatewayConfigRules">
        <b-form
          id="gateway-fee-configuration"
          @submit.prevent="handleSubmit"
        >
          <b-row>
            <b-col
              cols="12"
            >
              <h5 class="section-label mb-2 mt-2">
                {{ $t('Transaction Amount &amp; Fees Setup') }}
              </h5>
              <b-row class="match-height">
                <b-col md="6">
                  <SelectField
                    id="gateway-provider"
                    v-model="transactionFormData.gateway"
                    :label="$t('Gateway Provider')"
                    :options="gatewayProviderOptions"
                    :error-name="$t('Gateway Provider')"
                    rules="required"
                    item="label"
                    :reduce="option => option"
                    :placeholder="$t('Select')"
                    :readonly="isReadonly"
                    :set-selected="setFeeConfigurationByGatewayProvider"
                  />
                </b-col>
              </b-row>

              <div class="d-flex align-items-center justify-content-lg-end">
                <Button
                  :label="$t('Add')"
                  size="md"
                  icon="PlusIcon"
                  show-icon
                  class="mr-1 mb-2"
                  @click="addNewTransaction"
                />
              </div>
              <div
                v-for="(item, index) in transactionFormData.feeBands"
                :id="item.id"
                :key="item.id"
                ref="gatewayConfigRef"
              >
                <div class="d-flex align-items-center justify-content-lg-end">
                  <b-col
                    md="1"
                    class="mt-2"
                  >
                    <!-- Remove Button -->
                    <feather-icon
                      v-b-tooltip.hover.bottom="'Remove'"
                      icon="XIcon"
                      class="mr-25 cursor-pointer text-danger"
                      @click="removeTransactionItem(index)"
                    />

                  </b-col>
                </div>
                <b-row
                  class="match-height mb-3"
                >
                  <b-col md="3">
                    <InputNumberField
                      id="minimum"
                      v-model="item.minAmount"
                      :label="$t('Transfer Amount (Minimum)')"
                      :placeholder="$t('Minimum')"
                      rules="required|positive"
                      :error-name="$t('Minimum')"
                    />
                  </b-col>
                  <b-col md="3">
                    <InputNumberField
                      id="maximum"
                      v-model="item.maxAmount"
                      label="Transfer Amount (Maximum)"
                      :placeholder="$t('Maximum')"
                      rules="required|positive"
                      :error-name="$t('Maximum')"
                    />
                  </b-col>

                  <b-col md="3">
                    <InputNumberField
                      id="gateway-fee"
                      v-model="item.gatewayFee"
                      :label="$t('Gateway Fee')"
                      :placeholder="$t('Enter Gateway Fee')"
                      rules="required|positive"
                      :error-name="$t('Gateway Fee')"
                    />
                  </b-col>
                  <b-col md="3">
                    <InputField
                      id="gateway-fee-gl"
                      v-model="item.gatewayFeeGl"
                      :label="$t('Gateway Fee GL')"
                      :placeholder="$t('Enter Gateway Fee GL')"
                      type="number"
                      rules="required"
                      :error-name="$t('Gateway Fee GL')"
                    />
                  </b-col>
                  <b-col md="3">
                    <InputNumberField
                      id="institution-fee"
                      v-model="item.institutionFee"
                      :label="$t('Institution Fee')"
                      :placeholder="$t('Enter Institution Fee')"
                      rules="required|positive"
                      error-name="Institution Fee"
                    />
                  </b-col>
                  <b-col md="3">
                    <InputField
                      id="institution-fee-gl"
                      v-model="item.institutionFeeGl"
                      :label="$t('Bank Income GL')"
                      :placeholder="$t('Enter Bank Income GL')"
                      type="number"
                      rules="required"
                      :error-name="$t('Bank Income GL')"
                    />
                  </b-col>
                  <b-col md="3">
                    <InputNumberField
                      id="vat-charge"
                      v-model="item.vatFee"
                      :label="$t('VAT Charge')"
                      :placeholder="$t('Enter VAT Charge')"
                      rules="required|positive"
                      error-name="VAT Charge"
                    />
                  </b-col>
                  <b-col md="3">
                    <InputField
                      id="vat-fee-gl"
                      v-model="item.vatFeeGl"
                      :label="$t('VAT GL Account')"
                      :placeholder="$t('Enter VAT GL Account')"
                      type="number"
                      rules="required"
                      :error-name="$t('VAT GL Account')"
                    />
                  </b-col>
                </b-row>
              </div>
              <div class="text-center">
                <Button
                  v-if="getUserPermission('CONFIGURE_TRANSFER_FEE')"
                  :label="buttonLabel"
                  :loading="isLoading"
                  :disabled="isDisabled"
                  size="lg"
                  @click="handleSubmit"
                />
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </div>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol,
  BForm, VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import {
  required, amount, positive,
} from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import {
  toAmount, toBalance, toKobo,
} from '@/helpers/formatAmount'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import getStatusColor from '../../../../../helpers/getStatusColor'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    ValidationObserver,
    InputField,
    InputNumberField,
    SelectField,
    FullScreenSpinner,
    Button,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      showSpinner: false,
      buttonLabel: '',
      isDisabled: false,
      isCustomDisabled: false,
      isLoading: false,
      isEditable: false,
      required,
      toAmount,
      toBalance,
      amount,
      positive,
      getStatusColor,
      modalTitle: this.$t('Configure Transfer Gateway'),
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Gateway Provider Name' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'transferType', sortable: true, label: 'Transfer Option' },
        { key: 'status', sortable: true },
        { key: 'actions' },
      ],
      gatewayProviderOptions: [],

      transactionFormData: {
        gateway: '',
        feeBands: [],
        id: '',
      },
    }
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchFeeConfiguration()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchFeeConfiguration()
    },

  },
  mounted() {
    this.fetchFeeConfiguration()
    this.fetchGatewayProviders()
  },
  methods: {
    ...mapActions({
      getFeeConfigurations: 'eChannels/getFeeConfigurations',
      getFeeConfigurationsByGateway: 'eChannels/getFeeConfigurationsByGateway',
      createFeeConfiguration: 'eChannels/createFeeConfiguration',
      updateFeeConfiguration: 'eChannels/updateFeeConfiguration',
      getGatewayProviders: 'eChannels/getGatewayProviders',
    }),
    checkNumberLimit(e) {
      return String(e).substring(0, 3)
    },
    addNewTransaction() {
      this.transactionFormData.feeBands.push({
        minAmount: '',
        maxAmount: '',
        gatewayFee: '',
        gatewayFeeGl: '',
        institutionFee: '',
        institutionFeeGl: '',
        vatFee: '',
        vatFeeGl: '',
      })
    },
    removeTransactionItem(index) {
      this.transactionFormData.feeBands.splice(index, 1)
      this.trTrimHeight(this.$refs.gatewayConfigRef[0].offsetHeight)
    },
    handleSubmit() {
      this.$refs.gatewayConfigRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisText = this.$t('this Fee Configuration Setup?')
          this.confirm(this.gatewayConfigAction, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
        }
      })
    },
    async gatewayConfigAction() {
      try {
        this.isLoading = true
        if (this.transactionFormData.feeBands.length) {
          const transactions = this.transactionFormData.feeBands.map(items => ({
            ...items,
            minAmount: Number(toKobo(items.minAmount).toFixed()),
            maxAmount: Number(toKobo(items.maxAmount).toFixed()),
            gatewayFee: Number(toKobo(items.gatewayFee).toFixed()),
            institutionFee: Number(toKobo(items.institutionFee).toFixed()),
            vatFee: Number(toKobo(items.vatFee).toFixed()),
          }))
          const formData = {
            gateway: this.transactionFormData.gateway,
            feeBands: transactions,
          }
          const formData2 = {
            gateway: this.transactionFormData.gateway,
            id: this.transactionFormData.id,
            feeBands: transactions,
          }
          const response = this.buttonLabel === 'Save'
            ? await this.createFeeConfiguration(formData)
            : await this.updateFeeConfiguration(formData2)

          const { status } = await response
          if (status === 200) {
            this.fetchFeeConfiguration()
            const requestText = this.$t('Your request to')
            const feeText = this.$t('Fee Configuration Setup was successful')
            this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${feeText}` })
          }
        } else {
          this.alertError({ text: this.$t('You did not add fee configuration on this gateway') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestText = this.$t('Your request to')
          const reasonText = this.$t('Fee Configuration Setup failed. Reason')
          const errorMessage = `${requestText} ${this.buttonLabel} ${reasonText}: ${responseError.errorMessage}`
          this.alertError({ text: errorMessage })
        })
      } finally {
        this.isLoading = false
      }
    },
    async setFeeConfigurationByGatewayProvider(selectedValue) {
      try {
        this.isBusy = true
        const response = await this.getFeeConfigurationsByGateway(selectedValue)
        const { status, data } = response
        if (data.data === null) {
          this.buttonLabel = this.$t('Save')
          this.transactionFormData.feeBands = []
          this.transactionFormData.id = ''
          return
        }
        this.buttonLabel = this.$t('Update')
        if (status === 200 && data.data) {
          data.data.forEach(items => {
            this.transactionFormData.gateway = items.gateway
            this.transactionFormData.id = items.id
            this.transactionFormData.feeBands = items.feeBands.map(item => ({
              minAmount: toBalance(item.minAmount),
              maxAmount: toBalance(item.maxAmount),
              gatewayFee: toBalance(item.gatewayFee),
              gatewayFeeGl: item.gatewayFeeGl,
              institutionFee: toBalance(item.institutionFee),
              institutionFeeGl: item.institutionFeeGl,
              vatFee: toBalance(item.vatFee),
              vatFeeGl: item.vatFeeGl,
            }))
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchFeeConfiguration() {
      try {
        this.isBusy = true
        this.showSpinner = true
        this.hideModal('modal-filter')
        const response = await this.getFeeConfigurations({
          search: '',
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.CONFIGURE_TRANSFER_FEE,
        })
        const { status, data } = response
        if (data.data === null) {
          this.buttonLabel = this.$t('Save')
          return
        }
        this.buttonLabel = this.$t('Update')
        if (status === 200 && data.data) {
          data.data.forEach(items => {
            this.transactionFormData.gateway = items.gateway
            this.transactionFormData.id = items.id
            this.transactionFormData.feeBands = items.feeBands.map(item => ({
              minAmount: toBalance(item.minAmount),
              maxAmount: toBalance(item.maxAmount),
              gatewayFee: toBalance(item.gatewayFee),
              gatewayFeeGl: item.gatewayFeeGl,
              institutionFee: toBalance(item.institutionFee),
              institutionFeeGl: item.institutionFeeGl,
              vatFee: toBalance(item.vatFee),
              vatFeeGl: item.vatFeeGl,
            }))
          })
          this.totalCount = data.recordCount
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
        this.showSpinner = false
      }
    },
    async fetchGatewayProviders() {
      try {
        const response = await this.getGatewayProviders({ permission: eChannelsPermission.CONFIGURE_TRANSFER_FEE })
        const { status, data } = await response
        if (status === 200 && data) {
          this.gatewayProviderOptions = data
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
  },
}
</script>
